import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../shared/components/Layout';
import { OrderClItem, FlatRateLensOrderParams } from '../types';
import { convertKeysToSnakeCase } from '@/utils/format';
import createFlatRateLens from '../api/createFlatRateLens';
import { formatMonth } from '@/utils/format';
import { useOrderContext } from '@/providers/OrderProvider';
import InfoDisplay from '@/components/InfoDisplay';

const FlatRateLensConfirm: React.FC = () => {
  const navigate = useNavigate();
  const { arrivalTimeZoneLabels, orderClassificationLabels } = useOrderContext();

  const [facilityName, setFacilityName] = useState<string>('');
  const [facilityCode, setFacilityCode] = useState<string>('');
  const [flatRateCode, setFlatRateCode] = useState<string>('');
  const [facilityId, setFacilityId] = useState<number | undefined>();
  const [expectedShipDate, setExpectedShipDate] = useState<string>('');
  const [shortestArrivalDate, setShortestArrivalDate] = useState<string>('');

  const [custNameKana, setCustNameKana] = useState<string>();
  const [custCodeSuffix, setCustCodeSuffix] = useState<string>('');
  const [orderClassification, setOrderClassification] = useState<string>('brand_new');
  const [note, setNote] = useState<string>();
  const [arrivalDate, setArrivalDate] = useState<string>('');
  const [arrivalTimeZone, setArrivalTimeZone] = useState<string>('no_preference');
  const [rightLens, setRightLens] = useState<OrderClItem>({
    fc: undefined,
    tp: undefined,
    cyl: undefined,
    dia: undefined,
    qty: 1,
    eyeType: "right",
    productId: undefined
  });
  const [leftLens, setLeftLens] = useState<OrderClItem>({
    fc: undefined,
    tp: undefined,
    cyl: undefined,
    dia: undefined,
    qty: 1,
    eyeType: "left",
    productId: undefined
  });
  const [isRightLensOrdered, setIsRightLensOrdered] = useState<boolean>(true);
  const [isLeftLensOrdered, setIsLeftLensOrdered] = useState<boolean>(true);
  const [startMonth, setStartMonth] = useState<string>();
  const [productName, setProductName] = useState<string>();

  useEffect(() => {
    const storedOrderParams = localStorage.getItem('orderParams');

    if (storedOrderParams) {
      const orderParams = JSON.parse(storedOrderParams);

      const {
        custNameKana,
        orderClassification,
        note,
        expectedShipDate,
        shortestArrivalDate,
        arrivalDate,
        arrivalTimeZone,
        orderClItemsAttributes,
        custCodeSuffix,
      } = orderParams.order;
    
      const rightLens = orderClItemsAttributes.find((lens: OrderClItem) => lens.eyeType === 'right');
      const leftLens = orderClItemsAttributes.find((lens: OrderClItem) => lens.eyeType === 'left');

      setCustNameKana(custNameKana);
      setOrderClassification(orderClassification);
      setNote(note);
      setExpectedShipDate(expectedShipDate);
      setShortestArrivalDate(shortestArrivalDate);
      setArrivalDate(arrivalDate);
      setArrivalTimeZone(arrivalTimeZone);
      setRightLens(rightLens);
      setLeftLens(leftLens);
      setCustCodeSuffix(custCodeSuffix);
    }

    const storedFacilityName = localStorage.getItem("facilityName");
    if(storedFacilityName) {
      setFacilityName(JSON.parse(storedFacilityName));
    }

    const storedFacilityCode = localStorage.getItem("facilityCode");
    if(storedFacilityCode) {
      setFacilityCode(JSON.parse(storedFacilityCode));
    }

    const storedFlatRateCode = localStorage.getItem("flatRateCode");
    if(storedFlatRateCode) {
      setFlatRateCode(JSON.parse(storedFlatRateCode));
    }

    const storedFacilityId = localStorage.getItem("facilityId");
    if(storedFacilityId) {
      setFacilityId(JSON.parse(storedFacilityId));
    }

    const storedIsRightLensOrdered = localStorage.getItem("isRightLensOrdered");
    if(storedIsRightLensOrdered) {
      setIsRightLensOrdered(JSON.parse(storedIsRightLensOrdered));
    }

    const storedIsLeftLensOrdered = localStorage.getItem("isLeftLensOrdered");
    if(storedIsLeftLensOrdered) {
      setIsLeftLensOrdered(JSON.parse(storedIsLeftLensOrdered));
    }

    const storedProductName = localStorage.getItem("productName");
    if(storedProductName) {
      setProductName(JSON.parse(storedProductName));
    }

    const storedStartMonth = localStorage.getItem("startMonth");
    if(storedStartMonth) {
      setStartMonth(JSON.parse(storedStartMonth));
    }
  }, []);

  const convertedArrivalTimeZoneLabels = convertKeysToSnakeCase(arrivalTimeZoneLabels);
  const convertedOrderClassificationLabels = convertKeysToSnakeCase(orderClassificationLabels);

  const handleOnClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const params: FlatRateLensOrderParams = {
      order: {
        order_type: 'flat_rate',
        facilityId: facilityId,
        custNameKana: custNameKana,
        orderClassification: orderClassification,
        note: note,
        arrivalDate: arrivalDate,
        arrivalTimeZone: arrivalTimeZone,
        expectedShipDate: expectedShipDate,
        shortestArrivalDate: shortestArrivalDate,
        orderClItemsAttributes: [rightLens, leftLens],
        custCodeSuffix: custCodeSuffix
      }
    }

    try {
      const res = await createFlatRateLens(params);
      if(res.status === 201) {
        localStorage.removeItem("orderParams");
        localStorage.removeItem("facilityName");
        localStorage.removeItem("facilityCode");
        localStorage.removeItem("flatRateCode");
        localStorage.removeItem("facilityId");
        localStorage.removeItem("isRightLensOrdered");
        localStorage.removeItem("isLeftLensOrdered");
        localStorage.removeItem("productName");
        localStorage.removeItem("startMonth");

        navigate("/facility/orders/flat_rate_lens/complete");
      } else if(res.status !== 201) {
        navigate("/facility/orders/flat_rate_lens");
        window.scrollTo(0, 0);
        console.error(`status: ${res.status}, message: ${res.statusText}`);
      }
    } catch (err: any) {
      console.log('An unexpected error occurred:', err.message);
    }
  }

  const renderPreviousPage = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    navigate("/facility/orders/flat_rate_lens", { state: { fromConfirmPage: true } });
    window.scrollTo(0, 0);
  }

  return (
    <React.Fragment>
      <Layout>
        <main id="page-facility_login" className="wrapper">
          <section className="sec-contact">
            <div className="inner">
              <h1 className="c-ttl__01">定額制レンズ注文確認</h1>
              <div className="contact-box">
                <div className="contact-box__left">
                  <div className="mbpc-98 mbsp-32">
                    <form action="/">
                      <div className="contact-box__form js-item" id="id-01">
                        <h2 className="c-ttl__02">お客様情報</h2>
                        <InfoDisplay label="コードNo" value={facilityCode} />
                        <InfoDisplay label="得意先名" value={facilityName} />
                        <InfoDisplay label="お客様ID" value={`${flatRateCode}${custCodeSuffix}`} />
                        <InfoDisplay label="お客様名 (カナ)" value={custNameKana} />
                      </div>
                      <div className="contact-box__form mbsp-29 js-item" id="id-02">
                        <h2 className="c-ttl__02">注文内容</h2>
                        <InfoDisplay label="注文区分" value={convertedOrderClassificationLabels[orderClassification]} />
                        { (orderClassification !== "brand_new" && orderClassification !== "lost_non_warranty") &&
                          <InfoDisplay label="サービス利用開始月" value={formatMonth(startMonth)} />
                        }
                        <InfoDisplay label="対象レンズ" value={productName} />
                        <div className="contact-box__item contact-box__item--start">
                          <label className="contact-box__ttl">
                            <span>レンズデータ</span>
                          </label>
                          <div className="contact-box__ct">
                            <p className="contact-box__desc mbpc-15 mbsp-15"><b>右 (R)</b><br/>
                              {isRightLensOrdered ?
                                `FC：${rightLens.fc} / TP：${rightLens.tp} ${rightLens?.cyl ? `/ CYL：${rightLens?.cyl}` : ""} / DIA：${rightLens.dia} 数量 ${rightLens.qty}`
                                : "注文なし"
                              }
                            </p>
                            <p className="contact-box__desc"><b>左 (L)</b><br/>
                              {isLeftLensOrdered ?
                                `FC：${leftLens.fc} / TP：${leftLens.tp} ${rightLens?.cyl ? `/ CYL：${rightLens?.cyl}` : ""} / DIA：${leftLens.dia} 数量 ${leftLens.qty}`
                                : "注文なし"
                              }
                            </p>
                          </div>
                        </div>
                        <InfoDisplay label="備考" value={note} />
                        <InfoDisplay label="着日指定" value={arrivalDate ? arrivalDate : "最短お届け日"} />
                        <InfoDisplay label="ご希望時間帯" value={convertedArrivalTimeZoneLabels[arrivalTimeZone]} />
                      </div>
                      <button className="c-btn__01" onClick={handleOnClick}>注文確定</button>
                      <div className="c-al--c">
                        <button className="c-btn__02" onClick={renderPreviousPage}>前のページに戻る</button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
      </Layout>
    </React.Fragment>
  )
}

export default FlatRateLensConfirm;